import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { InvoiceService, PaymentService } from "@services";
import { INVOICE_STATUSES, CARD_ICONS } from "@constants";
import utils from "@utils";
import { AddPaymentMethodModal, Image } from "../../../components";
import { PaymentMethods } from "./modals/PaymentMethods/PaymentMethods";
import { ReviewPayment } from "./modals/ReviewPayment/ReviewPayment";

import "./PaymentSummary.scss";

export class PaymentSummary extends Component {
	constructor(props) {
		super(props);
		this.state = {
			invoiceLoading: false,
			invoiceIds: [],
			invoiceSummaryItems: [],
			paymentMethods: [],
			selectedMethod: {},
			loadingPaymentMethods: false,
			TotalOfficerPay: 0,
			TotalOrganizationFee: 0,
			TotalJurisdictionFee: 0,
			RKPayFee: 0,
			Tax: 0,
			Subtotal: 0,
			Total: 0,
		};
		this._paymentMethodsModal = createRef();
		this._reviewPaymentModal = createRef();
		this._addPaymentMethodModal = createRef();
	}

	componentDidMount() {
		const {
			location: { query, state: invoiceParams },
		} = this.props;
		// if not paying all invoices, redirect the user to the payments list on reload
		if (query?.payAll === undefined && invoiceParams?.InvoiceIds === undefined)
			window.location.href = "/payments/list/payment";
		this.setState({ invoiceIds: invoiceParams?.InvoiceIds }, () => {
			this.getInvoiceSummary();
		});

		this.getPaymentMethods();
	}

	/** Retrieves a list of invoices and summarizes the total payable amount */
	getInvoiceSummary = () => {
		const { lang } = global;
		const { invoiceIds } = this.state;
		const {
			location: { state: invoiceParams },
		} = this.props;
		this.setState({ invoiceLoading: true });
		const params = {
			queryParams: invoiceParams?.filters,
			data: invoiceIds?.length === 0 ? null : { invoiceIds },
		};
		InvoiceService.getInvoiceSummary(params)
			.then(({ data }) => {
				const { TransactionSummaryItems } = data;
				if (
					TransactionSummaryItems.length &&
					TransactionSummaryItems[0].InvoiceStatusId === INVOICE_STATUSES.PAID
				) {
					utils.notify({
						type: "error",
						title: "Error",
						message: lang.t("The invoice is already paid"),
					});
				}
				this.setState({
					invoiceSummaryItems: TransactionSummaryItems,
					TotalOfficerPay: data.TotalOfficerPay,
					TotalOrganizationFee: data.TotalOrganizationFee,
					TotalJurisdictionFee: data.TotalJurisdictionFee,
					RKPayFee: data.RKPayFee,
					Tax: data.Tax,
					Subtotal: data.Subtotal,
					Total: data.Total,
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading invoices"),
					timeOut: 1500,
				});
			})
			.finally(() => {
				this.setState({ invoiceLoading: false });
			});
	};

	/** Retrieves a list of payment methods a user has set up in their account */
	getPaymentMethods = () => {
		const { lang } = global;
		this.setState({ loadingPaymentMethods: true });

		PaymentService.getData()
			.then(({ data }) => {
				let paymentMethods = [];
				let selectedMethod = null;

				if (data) {
					paymentMethods = utils.extractPaymentMethods(data);
					selectedMethod = utils.getDefaultPaymentMethod(paymentMethods);
				}

				this.setState({ loadingPaymentMethods: false, selectedMethod, paymentMethods });
			})
			.catch(({ response: { data } }) => {
				this.setState({
					loadingPaymentMethods: false,
					selectedMethod: null,
					paymentMethods: [],
				});
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error loading Payment Methods"),
					title: "Oops",
				});
			});
	};

	openPaymentMethodsModal = () => {
		this._paymentMethodsModal.openModal();
	};

	openReviewPaymentModal = () => {
		this._reviewPaymentModal.openModal();
	};

	openAddPaymentMethodModal = () => {
		this._paymentMethodsModal.resetModal();
		this._addPaymentMethodModal.openModal();
	};

	onPaymentMethodChange = selection => {
		const { selectedMethod } = this.state;

		if (selection && selection.id !== selectedMethod?.id) {
			this.setState({ selectedMethod: selection });
		}
	};

	render() {
		const { lang } = global;
		const {
			invoiceLoading,
			paymentMethods,
			selectedMethod,
			loadingPaymentMethods,
			invoiceSummaryItems,
			TotalOfficerPay,
			TotalOrganizationFee,
			TotalJurisdictionFee,
			RKPayFee,
			Tax,
			Subtotal,
			Total,
		} = this.state;
		const {
			user,
			user: { TimeZoneId },
			router,
		} = this.props;
		const invoiceIdArray = invoiceSummaryItems.map(item => item.InvoiceId);

		return (
			<div id="payment-summary">
				<div className="container">
					<div className="row">
						<div className="col-xs-12">
							<h1>
								<i className="rkf rkf-receipt" />
								&nbsp; Your Invoice
							</h1>
						</div>
					</div>
					<div className="row invoice-table-header hidden-xs">
						<div className="col-sm-1">
							<strong>{lang.t("Invoice")}</strong>
						</div>
						<div className="col-sm-2">
							<strong>{lang.t("Date")}</strong>
						</div>
						<div className="col-sm-2">
							<strong>{lang.t("Location")}</strong>
						</div>
						<div className="col-sm-3">
							<strong>{lang.t("Officer")}</strong>
						</div>
						<div className="col-sm-1">
							<strong>{lang.t("Hours")}</strong>
						</div>
						<div className="col-sm-1 text-right">
							<strong>{lang.t("Rate/Hr")}</strong>
						</div>
						<div className="col-sm-2 text-right">
							<strong>{lang.t("Amount Due")}</strong>
						</div>
					</div>
					<div className="line-items">
						<If condition={invoiceLoading}>
							<div className="text-center">
								<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
							</div>
						</If>
						<For
							each="invoiceItem"
							of={invoiceSummaryItems || []}
							index="index"
						>
							<div
								key={`invoice-item_${invoiceItem.InvoiceId}`}
								className="row"
							>
								<div className="col-sm-1">
									<Link to={`/payments/${invoiceItem.InvoiceId}`}>
										<span className="visible-xs-inline">
											{lang.t("Invoice")} #
										</span>
										{invoiceItem.InvoiceId}
									</Link>
								</div>
								<div className="col-sm-2">
									{utils.formatDate(
										new Date(`${invoiceItem.StartDateTime}Z`),
										"en-US",
										TimeZoneId,
									)}
									<br />
									<small className="text-muted">
										{utils.formatTime(
											new Date(`${invoiceItem.StartDateTime}Z`),
											true,
											TimeZoneId,
										)}
										&nbsp;-&nbsp;
										{utils.formatTime(
											new Date(`${invoiceItem.EndDateTime}Z`),
											true,
											TimeZoneId,
										)}
									</small>
								</div>
								<div className="col-xs-12 col-sm-2">{invoiceItem.Location}</div>
								<div className="col-xs-12 col-sm-3">
									<strong className="visible-xs-inline">Officer:&nbsp;</strong>
									{invoiceItem.Officer}
								</div>
								<div className="col-xs-3 col-sm-1">
									{invoiceItem.Hours}
									<span className="visible-xs-inline">
										{invoiceItem.Hours === 1 ? " hr" : " hrs"}
									</span>
								</div>
								<div className="col-xs-3 col-sm-1 rate">
									{utils.currencyFormatter(invoiceItem.Rate)}
									<span className="visible-xs-inline">/hr</span>
								</div>
								<div className="col-xs-3 col-sm-2 text-right">
									<strong className="visible-xs-inline">
										{lang.t("Total")}:&nbsp;
									</strong>
									{utils.currencyFormatter(invoiceItem.TotalOfficerPay)}
								</div>
								<If condition={invoiceItem.JurisdictionFees.length > 0}>
									<div className="col-xs-12 col-sm-7 col-sm-offset-5">
										<strong>{lang.t("Additional Fees")}</strong>
									</div>
								</If>
								<For
									each="fee"
									of={invoiceItem.JurisdictionFees}
								>
									<div
										className="col-xs-12 fee-item"
										key={`${fee.InvoiceId}_${fee.ItemName.replace(" ", "")}`}
									>
										<div className="col-xs-12 col-sm-3 col-sm-offset-5">
											{fee.ItemName}
										</div>
										<div className="col-xs-3 col-sm-1">
											{fee.ItemHours}
											<span className="visible-xs-inline">
												{fee.ItemHours === 1 ? " hr" : " hrs"}
											</span>
										</div>
										<div className="col-xs-3 col-sm-1 rate">
											{utils.currencyFormatter(fee.ItemRate)}
											<span className="visible-xs-inline">/hr</span>
										</div>
										<div className="col-xs-3 col-sm-2 text-right">
											<strong className="visible-xs-inline">
												Total:&nbsp;
											</strong>
											{utils.currencyFormatter(fee.ItemSubtotal)}
										</div>
									</div>
								</For>
							</div>
						</For>
					</div>
					<If condition={invoiceSummaryItems?.length > 0}>
						<div className="invoice-totals">
							<div className="row">
								<div className="col-xs-8 col-sm-10 text-right">
									<strong>{lang.t("Total Officer Pay")}</strong>
								</div>
								<div className="col-xs-4 col-sm-2 text-right">
									{utils.currencyFormatter(TotalOfficerPay || 0)}
								</div>
							</div>
							<div className="row">
								<div className="col-xs-8 col-sm-10 text-right">
									<strong>{lang.t("Coordination Fees")}</strong>
								</div>
								<div className="col-xs-4 col-sm-2 text-right">
									{utils.currencyFormatter(TotalOrganizationFee || 0)}
								</div>
							</div>
							<div className="row">
								<div className="col-xs-8 col-sm-10 text-right">
									<strong>{lang.t("Jurisdiction Fees")}</strong>
								</div>
								<div className="col-xs-4 col-sm-2 text-right">
									{utils.currencyFormatter(TotalJurisdictionFee || 0)}
								</div>
							</div>
							<div className="row">
								<div className="col-xs-8 col-sm-10 text-right">
									<strong>{lang.t("Service Fees")}</strong>
								</div>
								<div className="col-xs-4 col-sm-2 text-right">
									{utils.currencyFormatter(RKPayFee || 0)}
								</div>
							</div>
							<div className="row">
								<div className="col-xs-8 col-sm-10 text-right">
									<strong>{lang.t("Tax")}</strong>
								</div>
								<div className="col-xs-4 col-sm-2 text-right">
									{utils.currencyFormatter(Tax || 0)}
								</div>
							</div>
							<div className="row">
								<div className="col-xs-12 col-sm-offset-8 col-sm-4">
									<div className="separator separator-solid" />
								</div>
							</div>
							<div className="row">
								<div className="col-xs-8 col-sm-10 text-right">
									<strong>{lang.t("Total")}</strong>
								</div>
								<div className="col-xs-4 col-sm-2 text-right">
									<strong>{utils.currencyFormatter(Total || 0)}</strong>
								</div>
							</div>
							<div className="row">
								<div className="col-xs-12 col-sm-offset-8 col-sm-4">
									<div className="payment-method">
										<Choose>
											<When condition={loadingPaymentMethods}>
												<div className="text-center">
													<i className="rkf rkf-spinner rkf-spin rkf-fw" />
												</div>
											</When>
											<Otherwise>
												<If condition={selectedMethod}>
													<div className="text-capitalize">
														<h5>{lang.t("Payment Method")}</h5>
														<Choose>
															<When
																condition={
																	selectedMethod.type ===
																	"bank_account"
																}
															>
																{`${selectedMethod.bankName} ${selectedMethod.accNumber}`}
															</When>
															<When
																condition={
																	selectedMethod.type === "card"
																}
															>
																<Image
																	className="payment-methods-card-image"
																	src={
																		CARD_ICONS[
																			selectedMethod.cardType
																		] || CARD_ICONS.default
																	}
																/>
																{`${selectedMethod.cardNumber}`}
															</When>
														</Choose>
													</div>
												</If>
												<div className="text-right">
													<button
														type="button"
														className="btn btn-link"
														onClick={() =>
															this.openPaymentMethodsModal()
														}
													>
														{!selectedMethod
															? lang.t("Select Payment Method")
															: lang.t("Change")}
													</button>
												</div>
											</Otherwise>
										</Choose>
									</div>
								</div>
							</div>
						</div>
						<div className="row">
							<div className="col-sm-offset-9 col-sm-3">
								<button
									type="button"
									className="btn btn-primary btn-block"
									onClick={() => this.openReviewPaymentModal()}
								>
									{lang.t("Approve Payment")}
								</button>
							</div>
						</div>
					</If>
					<PaymentMethods
						ref={ref => {
							this._paymentMethodsModal = ref;
						}}
						paymentMethods={paymentMethods}
						selectedMethod={selectedMethod}
						onConfirm={this.onPaymentMethodChange}
						openAddPaymentMethodModal={this.openAddPaymentMethodModal}
					/>
					<ReviewPayment
						ref={ref => {
							this._reviewPaymentModal = ref;
						}}
						subTotal={Subtotal}
						taxes={Tax + RKPayFee}
						total={Total}
						activeInvoiceIds={invoiceIdArray}
						paymentMethodId={selectedMethod?.id}
						push={router.push}
					/>
					<AddPaymentMethodModal
						user={user}
						callback={this.getPaymentMethods}
						ref={ref => {
							this._addPaymentMethodModal = ref;
						}}
					/>
				</div>
			</div>
		);
	}
}

PaymentSummary.propTypes = {
	location: PropTypes.object,
	user: PropTypes.object,
	router: PropTypes.object,
};

PaymentSummary.defaultProps = {
	location: {},
	user: {},
	router: {},
};

export default PaymentSummary;
